// The SHOP — a player-scaled building you walk into to buy weapons, ammo and upgrades. The buy logic
// (costOf / tryBuy / applyPerk / prompt) is ported from zombies' PerkSystem. Placement is the key
// design choice: the building sits JUST BEYOND the start pad's back edge, on its own floor slab that
// is NOT part of the NPC nav — so the army/tanks (which only ever walk nav cells) can't reach it and
// never clip its walls, while the PLAYER walks in through the doorway via a shop-floor ground override
// (main.collidePlayer). Dimensions are sized off PLAYER.eyeHeight so it fits a little-person.

import * as THREE from "three";
import { CELL, PLAYER, topY } from "../config";
import {
  MYSTERY_POOLS, PERKS, SHOP_STATIONS, STACKABLE_PERKS, type ShopStation,
} from "../data/arsenal";
import type { NavGraph } from "./pathfind";
import type { Player } from "../entities/player";
import type { WeaponSystem } from "./weapons";
import { sfx } from "../engine/sfx";

export interface WallBox {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
}

export interface BuyResult {
  spent: number;
  ok: boolean;
  message: string;
}

const E = PLAYER.eyeHeight; // player scale (≈20 studs) — everything derives from it
const W = 14 * E; // footprint width (perpendicular to the door)   ≈ 280
const D = 22 * E; // footprint depth (along the door normal)        ≈ 440
const H = 2.6 * E; // wall height                                   ≈ 52
const T = 0.6 * E; // wall thickness                                ≈ 12
const DOOR = 3.2 * E; // doorway gap width                          ≈ 64
const REACH = 4.5 * E; // how close (studs) you stand to buy        ≈ 90
const PILLAR_W = 0.7 * E;
const PILLAR_H = 1.7 * E;

const WALL_MAT = new THREE.MeshLambertMaterial({ color: 0x3b4048 });
const FLOOR_MAT = new THREE.MeshLambertMaterial({ color: 0x2a2f37 });

export class Shop {
  private group = new THREE.Group();
  private stations: Array<{ st: ShopStation; pos: THREE.Vector3 }> = [];
  private owned = new Set<string>();
  private bought = new Map<string, number>();
  private nearby: ShopStation | null = null;
  walls: WallBox[] = [];
  footprint: WallBox | null = null; // interior+walls rect; the player gets its floor even off-nav
  floorY = topY(0);
  spawn = new THREE.Vector3();
  private built = false;

  constructor(scene: THREE.Scene, private player: Player, private weapons: WeaponSystem) {
    scene.add(this.group);
  }

  /** (Re)build the shop for the current level: attached to the back of the start pad, off the nav. */
  build(nav: NavGraph): void {
    this.disposeMesh();
    this.stations = [];
    this.walls = [];
    this.footprint = null;

    const floorY = topY(0);
    this.floorY = floorY;
    const start = centroid(nav, nav.startKeys);
    const core = centroid(nav, nav.coreKeys);
    if (!start) {
      this.built = false;
      return;
    }
    // door faces the core (axis-snapped); the body sits BEHIND the pad (opposite the core)
    let nx = 0;
    let nz = 1;
    if (core) {
      const dx = core.x - start.x;
      const dz = core.z - start.z;
      if (Math.abs(dx) > Math.abs(dz)) { nx = Math.sign(dx) || 1; nz = 0; }
      else { nx = 0; nz = Math.sign(dz) || 1; }
    }
    const bkx = -nx; // back direction
    const bkz = -nz;
    // how far the pad reaches backward (cell centers) + half a cell to reach its true back edge
    let padBack = 0;
    for (const k of nav.startKeys) {
      const p = nav.point.get(k);
      if (!p) continue;
      const d = (p.x - start.x) * bkx + (p.z - start.z) * bkz;
      if (d > padBack) padBack = d;
    }
    const frontDist = padBack + CELL / 2; // pad's back edge along backDir
    const cx = start.x + bkx * (frontDist + D / 2); // shop center (behind the edge)
    const cz = start.z + bkz * (frontDist + D / 2);
    const alongX = nz === 0;
    const hx = alongX ? D / 2 : W / 2;
    const hz = alongX ? W / 2 : D / 2;
    this.footprint = { x0: cx - hx, x1: cx + hx, z0: cz - hz, z1: cz + hz };

    // floor slab (its own floor — the pad's nav doesn't extend here)
    const floor = new THREE.Mesh(new THREE.BoxGeometry(2 * hx, 8, 2 * hz), FLOOR_MAT);
    floor.position.set(cx, floorY - 4, cz);
    floor.receiveShadow = true;
    this.group.add(floor);

    // 4 walls, doorway on the +core (front) face
    const half = { x: hx, z: hz };
    this.addWallRun(cx, cz, floorY, "x", +1, half, nx === 1);
    this.addWallRun(cx, cz, floorY, "x", -1, half, nx === -1);
    this.addWallRun(cx, cz, floorY, "z", +1, half, nz === 1);
    this.addWallRun(cx, cz, floorY, "z", -1, half, nz === -1);

    // stations: two columns running along the depth axis (core axis), inset from the walls
    const caX = nx;
    const caZ = nz; // toward the door
    const ppX = nz;
    const ppZ = -nx; // perpendicular
    const alongHalf = D / 2 - 2.4 * E;
    const perpOff = W * 0.28;
    const n = SHOP_STATIONS.length;
    const perCol = Math.ceil(n / 2);
    SHOP_STATIONS.forEach((st, i) => {
      const first = i < perCol;
      const col = first ? -1 : 1;
      const row = first ? i : i - perCol;
      const rows = first ? perCol : n - perCol;
      const t = rows > 1 ? row / (rows - 1) : 0.5;
      const along = -alongHalf + 2 * alongHalf * t;
      const perp = col * perpOff;
      const sx = cx + caX * along + ppX * perp;
      const sz = cz + caZ * along + ppZ * perp;
      const pos = new THREE.Vector3(sx, floorY, sz);
      this.stations.push({ st, pos });
      this.buildStationMesh(st, pos);
    });

    // player spawn: on the pad, just in front of the doorway, facing the core
    const target = new THREE.Vector3(cx + nx * (D / 2 + 6 * E), floorY, cz + nz * (D / 2 + 6 * E));
    this.spawn.copy(nearestWalkable(nav, target) ?? target);
    this.built = true;
  }

  private addWallRun(
    cx: number, cz: number, floorY: number,
    axis: "x" | "z", sign: number, half: { x: number; z: number }, door: boolean,
  ): void {
    const y = floorY + H / 2;
    if (axis === "x") {
      const wx = cx + sign * half.x;
      if (!door) {
        this.pushWall(wx - T / 2, wx + T / 2, cz - half.z, cz + half.z, y);
      } else {
        this.pushWall(wx - T / 2, wx + T / 2, cz - half.z, cz - DOOR / 2, y);
        this.pushWall(wx - T / 2, wx + T / 2, cz + DOOR / 2, cz + half.z, y);
      }
    } else {
      const wz = cz + sign * half.z;
      if (!door) {
        this.pushWall(cx - half.x, cx + half.x, wz - T / 2, wz + T / 2, y);
      } else {
        this.pushWall(cx - half.x, cx - DOOR / 2, wz - T / 2, wz + T / 2, y);
        this.pushWall(cx + DOOR / 2, cx + half.x, wz - T / 2, wz + T / 2, y);
      }
    }
  }

  private pushWall(x0: number, x1: number, z0: number, z1: number, cy: number): void {
    if (x1 - x0 <= 0 || z1 - z0 <= 0) return;
    this.walls.push({ x0, x1, z0, z1 });
    const m = new THREE.Mesh(new THREE.BoxGeometry(x1 - x0, H, z1 - z0), WALL_MAT);
    m.position.set((x0 + x1) / 2, cy, (z0 + z1) / 2);
    m.castShadow = true;
    m.receiveShadow = true;
    this.group.add(m);
  }

  private buildStationMesh(st: ShopStation, pos: THREE.Vector3): void {
    const g = new THREE.Group();
    g.position.copy(pos);
    const pillar = new THREE.Mesh(
      new THREE.BoxGeometry(PILLAR_W, PILLAR_H, PILLAR_W),
      new THREE.MeshLambertMaterial({
        color: st.color, emissive: st.color, emissiveIntensity: 0.4,
        transparent: true, opacity: 0.6, depthWrite: false,
      }),
    );
    pillar.position.set(0, PILLAR_H / 2, 0);
    g.add(pillar);
    const label = makeLabelSprite(st.label, st.color);
    label.position.set(0, PILLAR_H + 1.1 * E, 0);
    g.add(label);
    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.4 * E, 0.4 * E, 40 * E, 8, 1, true),
      new THREE.MeshBasicMaterial({
        color: st.color, transparent: true, opacity: 0.14, depthWrite: false,
        side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
      }),
    );
    beam.position.set(0, 20 * E, 0);
    g.add(beam);
    this.group.add(g);
  }

  /** Per-frame: which station (if any) is in reach. */
  update(): void {
    if (!this.built) {
      this.nearby = null;
      return;
    }
    const px = this.player.position.x, pz = this.player.position.z;
    let best: ShopStation | null = null;
    let bestD2 = REACH * REACH; // squared compare — no per-station sqrt
    for (const { st, pos } of this.stations) {
      const dx = px - pos.x, dz = pz - pos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        best = st;
      }
    }
    this.nearby = best;
  }

  private costOf(st: ShopStation): number {
    if (st.kind === "weapon" || st.kind === "maxammo") return st.cost;
    if (st.kind === "pap") return Math.round(st.cost * Math.pow(1.5, this.weapons.papLevel));
    return Math.round(st.cost * Math.pow(3, this.bought.get(st.id) ?? 0));
  }

  get prompt(): string | null {
    const st = this.nearby;
    if (!st) return null;
    if (st.kind === "perk" && this.owned.has(st.refId) && !STACKABLE_PERKS.has(st.refId)) {
      return `${st.label} — OWNED`;
    }
    let noun: string;
    switch (st.kind) {
      case "perk": noun = PERKS[st.refId].effect; break;
      case "mystery": noun = st.refId === "melee" ? "random melee" : "random weapon"; break;
      case "pap": noun = "upgrade your weapon"; break;
      case "maxammo": noun = "refill all ammo"; break;
      default: noun = "weapon";
    }
    return `[E] ${st.label}  (${this.costOf(st)}) — ${noun}`;
  }

  tryBuy(points: number): BuyResult {
    const st = this.nearby;
    if (!st) return { spent: 0, ok: false, message: "" };
    if (st.kind === "perk" && this.owned.has(st.refId) && !STACKABLE_PERKS.has(st.refId)) {
      return { spent: 0, ok: false, message: "Already owned" };
    }
    if (st.kind === "pap" && this.weapons.papLevel >= 10) {
      return { spent: 0, ok: false, message: "Weapon fully Pack-a-Punched (+10)" };
    }
    const cost = this.costOf(st);
    if (points < cost) {
      sfx.play("deny");
      return { spent: 0, ok: false, message: "Not enough points" };
    }

    let message = "";
    switch (st.kind) {
      case "perk":
        this.applyPerk(st.refId);
        this.owned.add(st.refId);
        message = `Bought ${st.label}`;
        break;
      case "weapon":
        this.weapons.add(st.refId, true);
        message = `Bought ${st.label}`;
        break;
      case "mystery": {
        const r = this.weapons.pickMystery(MYSTERY_POOLS[st.refId] ? st.refId : "guns");
        this.weapons.add(r.id, true);
        message = `Got ${r.name}!`;
        break;
      }
      case "pap": {
        const name = this.weapons.packAPunch();
        if (!name) return { spent: 0, ok: false, message: "Weapon fully Pack-a-Punched (+10)" };
        message = `Upgraded to ${name}`;
        break;
      }
      case "maxammo":
        this.weapons.refillAll();
        message = "MAX AMMO";
        break;
    }
    sfx.play("buy");
    if (st.kind === "perk" || st.kind === "mystery") {
      this.bought.set(st.id, (this.bought.get(st.id) ?? 0) + 1);
    }
    return { spent: cost, ok: true, message };
  }

  private applyPerk(id: string): void {
    switch (id) {
      case "juggernog":
        this.player.maxHealth += 100;
        this.player.health = this.player.maxHealth;
        break;
      case "staminup":
        this.player.speedMul = 1.4;
        break;
      case "speedcola":
        this.weapons.reloadMul = Math.max(0.25, this.weapons.reloadMul * 0.7);
        break;
      case "doubletap":
        this.weapons.fireRateMul = Math.max(0.4, this.weapons.fireRateMul * 0.78);
        break;
      case "deadshot":
        this.weapons.perkDamageMul *= 1.2;
        break;
      case "quickrevive":
        this.player.regenMul *= 1.6;
        break;
    }
  }

  private disposeMesh(): void {
    for (const child of [...this.group.children]) {
      this.group.remove(child);
      child.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        const mat = mesh.material ?? (o as unknown as THREE.Sprite).material;
        for (const m of Array.isArray(mat) ? mat : mat ? [mat] : []) {
          const tex = (m as THREE.SpriteMaterial).map;
          if (tex) tex.dispose();
          if (m !== WALL_MAT && m !== FLOOR_MAT) m.dispose();
        }
      });
    }
  }
}

function centroid(nav: NavGraph, keys: string[]): { x: number; z: number } | null {
  if (!keys.length) return null;
  let x = 0;
  let z = 0;
  let n = 0;
  for (const k of keys) {
    const p = nav.point.get(k);
    if (!p) continue;
    x += p.x;
    z += p.z;
    n++;
  }
  return n ? { x: x / n, z: z / n } : null;
}

/** The nav.point of the start-pad cell nearest `target` (a guaranteed-walkable spawn). */
function nearestWalkable(nav: NavGraph, target: THREE.Vector3): THREE.Vector3 | null {
  let best: THREE.Vector3 | null = null;
  let bd = Infinity;
  for (const k of nav.startKeys) {
    const p = nav.point.get(k);
    if (!p) continue;
    const d = (p.x - target.x) ** 2 + (p.z - target.z) ** 2;
    if (d < bd) {
      bd = d;
      best = new THREE.Vector3(p.x, p.y, p.z);
    }
  }
  return best;
}

function makeLabelSprite(text: string, color: number): THREE.Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(0, 0, 256, 64);
  ctx.font = "bold 30px 'Courier New', monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = `#${color.toString(16).padStart(6, "0")}`;
  ctx.fillText(text, 128, 34);
  const tex = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
  const sprite = new THREE.Sprite(mat);
  sprite.scale.set(3.2 * E, 0.8 * E, 1);
  return sprite;
}
